import React, { useState } from 'react';
import { Gender, HealthProfile } from '../healthTypes';
import { generateUUID } from '../utils/uuid';
import { User, Calendar, Target, Ruler } from 'lucide-react';

interface ProfileFormProps {
  initialData?: HealthProfile;
  onSave: (profile: HealthProfile) => void;
  onCancel: () => void;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({ initialData, onSave, onCancel }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [gender, setGender] = useState<Gender>(initialData?.gender || Gender.Male);
  const [birthDate, setBirthDate] = useState(initialData?.birthDate || '2000-01-01');
  const [targetWeight, setTargetWeight] = useState<string>(initialData?.targetWeight?.toString() || '');
  const [currentHeight, setCurrentHeight] = useState<string>(initialData?.height?.toString() || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !birthDate) return;

    onSave({
      id: initialData?.id || generateUUID(),
      name,
      gender,
      birthDate,
      targetWeight: targetWeight ? parseFloat(targetWeight) : undefined,
      height: currentHeight ? parseFloat(currentHeight) : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-100/80 dark:bg-slate-900/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 w-full max-w-sm rounded-2xl shadow-xl p-6">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
          {initialData ? '编辑档案' : '新建档案'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">昵称</label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-slate-400" size={18} />
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white"
                placeholder="请输入昵称"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">性别</label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setGender(Gender.Male)}
                className={`flex-1 py-3 rounded-xl border-2 font-medium transition-colors ${gender === Gender.Male ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'border-slate-100 dark:border-slate-600 text-slate-400'}`}
              >
                男
              </button>
              <button
                type="button"
                onClick={() => setGender(Gender.Female)}
                className={`flex-1 py-3 rounded-xl border-2 font-medium transition-colors ${gender === Gender.Female ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' : 'border-slate-100 dark:border-slate-600 text-slate-400'}`}
              >
                女
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">出生日期</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 text-slate-400" size={18} />
              <input
                type="date"
                value={birthDate}
                onChange={e => setBirthDate(e.target.value)}
                required
                className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white"
              />
            </div>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">用于精确计算年龄，判断 BMI 标准</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">目标体重 (kg)</label>
              <div className="relative">
                <Target className="absolute left-3 top-3 text-slate-400" size={18} />
                <input
                  type="number"
                  step="0.1"
                  value={targetWeight}
                  onChange={e => setTargetWeight(e.target.value)}
                  className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">默认身高 (cm)</label>
              <div className="relative">
                <Ruler className="absolute left-3 top-3 text-slate-400" size={18} />
                <input
                  type="number"
                  step="0.1"
                  value={currentHeight}
                  onChange={e => setCurrentHeight(e.target.value)}
                  className="w-full pl-10 p-3 bg-slate-50 dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <button type="button" onClick={onCancel} className="flex-1 py-3 text-slate-500 dark:text-slate-400 font-medium hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl">取消</button>
            <button type="submit" className="flex-[2] py-3 bg-emerald-500 text-white font-bold rounded-xl shadow-lg hover:bg-emerald-600">保存</button>
          </div>
        </form>
      </div>
    </div>
  );
};
